<script setup>
import { useStore } from '@/store/index.js'
import CTA from '@/components/CTA.vue'
import ArticleParagraph from '@/components/ArticleParagraph.vue'
import ArticlePicture from '@/components/ArticlePicture.vue'
import Image from '@/components/Image.vue'
import {
  ARTICLE_PARAGRAPH,
  CALL_TO_ACTION,
  PICTURE 
} from '@/utils/constants'
import { formatDate } from '@/utils/date.js'

definePageMeta({
  layout: 'article',
  name: 'article'
})
const store = useStore()
const route = useRoute()

const slug = route.params.slug
const article = store.articleBySlug(slug)
if (!article) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}
const title = article.attributes.title
const subtitle = article.attributes.subtitle
const date = formatDate(article.attributes.publishedAt)
const tag = article.attributes.tag
const coverObj = article.attributes.cover.data.attributes

function mapTypeToComp (type) {
    const map = {
      [ARTICLE_PARAGRAPH] : ArticleParagraph,
      [CALL_TO_ACTION] : CTA,
      [PICTURE] : ArticlePicture
    }
    return map[type]
}

useLyberBlogSeo(article)
</script>

<template>
  <section class="relative wrapper overflow-hidden bg-scroll pt-20">
    <Image
      :content="coverObj"
      :class-list="'object-cover w-full sm:block'"
    />
    <div class="absolute top-0 left-0 w-full h-full bg-gray-900 opacity-60"></div>
    <div class="absolute top-0 left-0 bottom-0 right-0 h-full text-white flex flex-col justify-center
      mt-10 sm:mt-0 py-10 mx-auto w-5/6 md:w-8/12 lg:w-1/2">
      <NuxtLink
        :to="{path: '/blog/'}" 
        class="mb-3 md:mb-5 font-bold text-lg xs:text-xl md:text-3xl flex items-center">
        <svg
          class="py-px"
          width="24"
          height="24"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"></path>
        </svg>
          Blog
      </NuxtLink>
      <h1 class="mb-3 font-bold text-xl xs:text-3xl md:text-5xl lg:text-6xl">{{ title }}</h1>
      <h2 class="hidden xs:block text-lg xs:text-xl md:text-3xl">{{ subtitle }}</h2>
      <div class="mt-6 md:mt-12 flex justify-end items-center">
        <NuxtLink :to="{ path: '/blog/tags/' + tag.data.attributes.slug + '/' }">
          <span class="bg-tag rounded-2xl text-sm xs:text-base md:text-xl px-3 md:px-4 py-1">{{ tag.data.attributes.name }}</span>
        </NuxtLink>
      </div>
    </div>
  </section>
  <article class="mt-24 mb-12">
    <div class="mx-auto w-5/6 md:w-8/12 lg:w-1/2 xl:w-2/5">
      <component v-for="content in article.attributes.content"
        :key="content.id"
        :is="mapTypeToComp(content.__typename)"
        :content="content" />
    </div>
  </article>
  <hr class="mb-2"/>
</template>

<style lang="scss" scoped>
.wrapper {
  max-height: 635px;
}
</style>
